import React, { useState, useEffect, useRef } from 'react';
import './AddressAutocomplete.css';
import { Coordinates } from '../types/event';

interface AddressAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onCoordinatesChange?: (coordinates: Coordinates | null) => void;
  onSelect?: (place: google.maps.places.PlaceResult) => void;
  placeholder?: string;
  className?: string;
  country?: string;
  required?: boolean;
  label?: string;
  showMap?: boolean;
  mapHeight?: string;
  disabled?: boolean;
  inputId?: string;
}

declare global {
  interface Window {
    google: any;
    initAutocomplete?: () => void;
  }
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onCoordinatesChange,
  onSelect,
  placeholder = 'Entrez une adresse',
  className = '',
  country = 'ca',
  required = false,
  label = 'Adresse',
  showMap = false,
  mapHeight = '200px',
  disabled = false,
  inputId = 'address-autocomplete',
}) => {
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);
  
  // Add ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Create refs to store services
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenCanvasRef = useRef<HTMLDivElement | null>(null);
  
  // Create hidden canvas on mount
  useEffect(() => {
    // Create a hidden div for PlacesService if it doesn't exist yet
    if (!showMap && !document.getElementById('map-canvas-hidden')) {
      console.log('Creating hidden map canvas for Places service on mount');
      const hiddenDiv = document.createElement('div');
      hiddenDiv.id = 'map-canvas-hidden';
      hiddenDiv.style.display = 'none';
      document.body.appendChild(hiddenDiv);
      hiddenCanvasRef.current = hiddenDiv;
    }
    
    return () => {
      isMounted.current = false;
      // Clean up hidden div if we created it
      if (!showMap && hiddenCanvasRef.current && document.body.contains(hiddenCanvasRef.current)) {
        console.log('Removing hidden canvas on unmount');
        document.body.removeChild(hiddenCanvasRef.current);
      }
    };
  }, [showMap]);
  
  // Function to initialize Google Maps services
  const initializeGoogleMapsServices = () => {
    console.log('Initializing Google Maps services');
    
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('Google Maps libraries loaded successfully');
      
      // Initialize session token
      if (!sessionToken) {
        setSessionToken(new google.maps.places.AutocompleteSessionToken());
        console.log('Created new session token');
      }
      
      // Initialize Autocomplete service
      autocompleteService.current = new google.maps.places.AutocompleteService();
      console.log('Autocomplete service initialized:', !!autocompleteService.current);
      
      // Initialize Places service if map is shown
      if (showMap && mapRef.current) {
        console.log('Initializing map-based Places service');
        const map = new google.maps.Map(mapRef.current, {
          center: { lat: 45.508888, lng: -73.561668 }, // Montreal by default
          zoom: 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        
        setMapInstance(map);
        
        // Create marker but don't place it yet
        const newMarker = new google.maps.Marker({
          map,
          draggable: false,
        });
        
        setMarker(newMarker);
        
        // Initialize Places service with the map
        placesService.current = new google.maps.places.PlacesService(map);
        console.log('Map-based Places service initialized:', !!placesService.current);
      } else if (hiddenCanvasRef.current) {
        // Create a hidden div for PlacesService if no map is shown
        console.log('Initializing hidden canvas Places service');
        placesService.current = new google.maps.places.PlacesService(hiddenCanvasRef.current);
        console.log('Hidden canvas Places service initialized:', !!placesService.current);
      } else {
        console.error('Could not initialize Places service - no canvas available');
      }
    } else {
      console.error('Google Maps libraries not loaded');
    }
  };
  
  // Load Google Maps script if not already loaded
  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      // Create the script element
      const googleMapsScript = document.createElement('script');
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
      
      // Show API key status
      console.log('Google Maps API Key available:', !!apiKey);
      
      if (!apiKey) {
        console.error('Google Maps API Key is missing. Add VITE_GOOGLE_MAPS_API_KEY to your .env file.');
        return;
      }
      
      googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initAutocomplete`;
      googleMapsScript.async = true;
      googleMapsScript.defer = true;
      
      // Define the callback function to initialize services
      window.initAutocomplete = initializeGoogleMapsServices;
      
      // Add the script to the DOM
      document.head.appendChild(googleMapsScript);
      
      // Clean up
      return () => {
        document.head.removeChild(googleMapsScript);
        if (window.initAutocomplete) {
          delete window.initAutocomplete;
        }
      };
    } else {
      // Google Maps is already loaded, initialize services
      initializeGoogleMapsServices();
    }
  }, [showMap]);
  
  // Function to get place details and update coordinates
  const getPlaceDetails = (placeId: string) => {
    if (!placesService.current) {
      console.error('Places service not initialized!');
      
      // Try to initialize it now if possible
      if (window.google && window.google.maps && window.google.maps.places) {
        console.log('Attempting to initialize Places service on-demand');
        if (hiddenCanvasRef.current) {
          placesService.current = new google.maps.places.PlacesService(hiddenCanvasRef.current);
          console.log('Places service initialized on-demand:', !!placesService.current);
        } else {
          console.error('No hidden canvas reference available for Places service');
          return;
        }
      } else {
        console.error('Google Maps libraries not available');
        return;
      }
    }
    
    if (!sessionToken) {
      console.log('Creating new session token');
      const newToken = new google.maps.places.AutocompleteSessionToken();
      setSessionToken(newToken);
      // Use the new token immediately
      fetchPlaceDetails(placeId, newToken);
    } else {
      fetchPlaceDetails(placeId, sessionToken);
    }
  };
  
  // Separate function to actually fetch place details
  const fetchPlaceDetails = (placeId: string, token: google.maps.places.AutocompleteSessionToken) => {
    console.log('Fetching place details for ID:', placeId);
    
    if (!placesService.current) {
      console.error('Places service is still null, cannot fetch place details');
      return;
    }
    
    placesService.current.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry', 'name', 'geometry.location'],
        sessionToken: token,
      },
      (place, status) => {
        console.log('Place details received:', place);
        console.log('Place details status:', status);
        
        if (status === "OK" && place) {
          // Update the input value with the formatted address
          onChange(place.formatted_address || '');
          
          // Extract coordinates
          if (place.geometry && place.geometry.location) {
            console.log('Place geometry location:', place.geometry.location);
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            console.log('Raw latitude:', lat);
            console.log('Raw longitude:', lng);
            
            const newCoordinates = {
              lat: lat,
              lon: lng,
            };
            
            // Add debug log for coordinates
            console.log('Extracted coordinates:', newCoordinates);
            
            // Call the onCoordinatesChange prop if provided
            if (onCoordinatesChange) {
              onCoordinatesChange(newCoordinates);
            }
            
            // Update map if shown
            if (showMap && mapInstance && marker) {
              mapInstance.setCenter(place.geometry.location);
              marker.setPosition(place.geometry.location);
              marker.setVisible(true);
            }
            
            // Call onSelect prop if provided
            if (onSelect) {
              onSelect(place);
            }
          }
          
          // Create a new session token after a selection
          setSessionToken(new google.maps.places.AutocompleteSessionToken());
        }
      }
    );
  };
  
  // Function to fetch autocomplete suggestions
  const fetchSuggestions = (input: string) => {
    if (!autocompleteService.current || input.length < 3 || !sessionToken) {
      setSuggestions([]);
      return;
    }
    
    const request = {
      input,
      sessionToken,
      componentRestrictions: { country }, // Limit to specific country
      types: ['address'],
    };
    
    autocompleteService.current.getPlacePredictions(
      request,
      (predictions, status) => {
        if (status === "OK" && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  };
  
  // Debounce input changes to reduce API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      if (value.length >= 3) {
        fetchSuggestions(value);
      } else {
        setSuggestions([]);
      }
    }, 300); // 300ms debounce
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, country]);
  
  // Effect to close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Track input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    
    // Reset coordinates if the input field is cleared
    if (e.target.value === '' && onCoordinatesChange) {
      onCoordinatesChange(null);
      
      // Hide marker on map if shown
      if (marker) {
        marker.setVisible(false);
      }
    }
  };
  
  // Handle focus on input
  const handleFocus = () => {
    if (value.length >= 3) {
      setShowSuggestions(true);
    }
  };
  
  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    console.log('Selection clicked:', suggestion.description);
    console.log('Place ID:', suggestion.place_id);
    
    // Close the suggestions dropdown immediately
    setShowSuggestions(false);
    // Get place details to extract coordinates and formatted address
    getPlaceDetails(suggestion.place_id);
    // Temporarily update the input value to give immediate feedback
    onChange(suggestion.description);
  };
  
  return (
    <div className={`address-autocomplete-container ${className}`} ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className="address-label">
          {label}{required && <span className="required-asterisk">*</span>}
        </label>
      )}
      
      <div className="autocomplete-input-container">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="autocomplete-input"
          autoComplete="off"
        />
        
        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-container">
            <ul className="suggestions-list">
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="suggestion-item"
                >
                  {suggestion.description}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {showMap && (
        <div
          ref={mapRef}
          className="map-container"
          style={{ height: mapHeight }}
        ></div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
